import type { UserAccountMembership as GQLItem } from '../../../generated/internal.graphql';
import * as Account from '../Account/Account';

/**
 * One of a user's account memberships: which account it grants access to, and in what role.
 *
 * Inside the `User/` domain rather than top-level next to `Account.ts`, because — unlike
 * `AccountSummary`, which several internal queries resolve — this type is reachable only through
 * `User.memberships`.
 *
 * `account` goes through the shared `AccountSummary` mapper, so a membership row links to the same
 * account record the superadmin account screens read.
 */
export type UserAccountMembership = {
  account: Account.Account;
  role?: string;
  teamRole?: string;
};

/**
 * Both roles are nullable on the schema and are normalised to `undefined`: nothing in the admin
 * renders a `null`, and a `v-if` on one would print the string rather than fall through to the
 * empty branch.
 *
 * `role` is set only for the account owner, so its absence means "not the owner" rather than
 * "unknown" — such a member carries a `teamRole` instead, and either may be missing.
 */
export const fromGql = (item: GQLItem): UserAccountMembership => ({
  account: Account.fromGql(item.account),
  role: item.role ?? undefined,
  teamRole: item.teamRole ?? undefined,
});
